import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Send, Loader2, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import { trackEmailClick } from "@/lib/api";
import { useLang } from "@/i18n";
import { useEmailSend } from "@/hooks/useEmailSend";

export const EmailItinerary = ({ tripId }) => {
  const { t } = useLang();
  const { email, setEmail, consent, setConsent, busy, submit } = useEmailSend(tripId, "box");
  const [sent, setSent] = useState(null);
  const [open, setOpen] = useState(false);
  const openForm = () => { setOpen(true); trackEmailClick(tripId); };
  const [before, after] = t("Accetto la {link} per ricevere l'itinerario e i prossimi consigli di viaggio").split("{link}");
  const onSent = (addr) => {
    setSent(addr);
    toast.success(t("Preventivo inviato! Trovi il PDF in allegato nella tua casella email."));
  };

  return (
    <div data-testid="email-itinerary-box" className="no-print rounded-3xl border border-emerald-100 bg-white p-8 md:p-10">
      <div className="flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-700"><Mail className="h-6 w-6" /></span>
        <div>
          <p className="font-display text-xl font-semibold text-gray-900">{t("Ricevi il preventivo gratuito via email")}</p>
          <p className="mt-1 text-sm text-gray-600">{t("Gratis: inserisci la tua email e ricevi il PDF completo in allegato, con il link per riaprire il viaggio quando vuoi.")}</p>
        </div>
      </div>
      {sent ? (
        <div data-testid="email-itinerary-success" className="mt-6 flex flex-wrap items-center gap-3 rounded-2xl bg-emerald-50 p-5 text-emerald-800">
          <CheckCircle2 className="h-5 w-5" /><span className="break-all font-semibold">{t("Inviato a {email}", { email: sent })}</span>
          <button data-testid="email-itinerary-again-button" onClick={() => setSent(null)} className="ml-auto text-sm font-semibold underline">{t("Invia a un altro indirizzo")}</button>
        </div>
      ) : !open ? (
        <button data-testid="email-cta-button" onClick={openForm}
          className="mt-6 inline-flex h-12 items-center justify-center gap-2 rounded-full bg-emerald-600 px-7 text-sm font-semibold text-white transition-colors hover:bg-emerald-500">
          <FileText className="h-4 w-4" />{t("Ricevi il preventivo gratuito via email")}
        </button>
      ) : (
        <form onSubmit={(e) => submit(e, onSent)} noValidate className="mt-6 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <input data-testid="email-itinerary-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("La tua email")}
              className="h-12 flex-1 rounded-full border border-gray-200 bg-slate-50 px-5 text-sm outline-none transition-colors focus:border-emerald-500 focus:bg-white" />
            <button data-testid="email-itinerary-submit-button" type="submit" disabled={busy}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-emerald-500 disabled:opacity-70">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{busy ? t("Invio in corso…") : t("Invia il preventivo")}
            </button>
          </div>
          <label className="flex cursor-pointer items-start gap-3 text-sm text-gray-600">
            <input data-testid="email-itinerary-consent-checkbox" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600" />
            <span>{before}<Link data-testid="email-itinerary-privacy-link" to="/privacy" target="_blank" className="font-semibold text-emerald-700 underline">{t("privacy policy")}</Link>{after}</span>
          </label>
        </form>
      )}
    </div>
  );
};
